import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import TrendingTicker from '../components/TrendingTicker';
import RelatedNews from '../components/RelatedNews';
import {
  MdGavel, MdLocationCity, MdAccountBalance, MdFlag,
  MdExpandMore, MdExpandLess, MdRefresh, MdInfo, MdThumbUp, MdThumbDown,
  MdAdd, MdAccessTime, MdNotifications, MdNotificationsActive, MdEmail, MdOutlineEmail,
  MdNewReleases, MdHistory, MdArrowForward, MdShare, MdCheck, MdHowToVote,
  MdVerified, MdOpenInNew, MdPerson, MdSearch, MdClose, MdFavorite
} from 'react-icons/md';

const SCOPES = [
  { key: 'local',    label: 'Local Bills',    icon: MdLocationCity, color: 'text-emerald-600', bg: 'bg-emerald-50',  ring: 'ring-emerald-200' },
  { key: 'state',    label: 'State Bills',    icon: MdAccountBalance, color: 'text-sky-600',    bg: 'bg-sky-50',      ring: 'ring-sky-200' },
  { key: 'national', label: 'National Bills', icon: MdFlag,           color: 'text-rose-600',   bg: 'bg-rose-50',     ring: 'ring-rose-200' },
];

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Updated today';
    if (days === 1) return 'Updated 1 day ago';
    return `Updated ${days} days ago`;
  } catch {
    return '';
  }
}

// Compute the same bill_key that the backend uses, so the frontend can match
// a rendered bill card to a stored subscription regardless of refresh order.
function normalizeName(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

async function sha1Hex16(input) {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-1', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

async function computeBillKey(billId, billName) {
  const bid = (billId || '').trim().toLowerCase();
  if (bid) return `id:${bid}`;
  const norm = normalizeName(billName || '');
  if (!norm) return 'unknown';
  return 'n:' + (await sha1Hex16(norm));
}

function BillCard({ bill, scopeColor, scopeKey, subscription, onToggleSubscribe, onToggleEmail, busy, highlight, registerRef, voteSummary, onVote, focused }) {
  const [expanded, setExpanded] = useState(false);
  const isSubscribed = !!subscription;
  const emailOn = !!(subscription && subscription.notify_email);
  const cardRef = useRef(null);
  const [history, setHistory] = useState(null); // null = not loaded; [] = loaded empty
  const [historyLoading, setHistoryLoading] = useState(false);
  const [shared, setShared] = useState(false);
  // LegiScan verification: null = not yet loaded; 'loading'; object = data; string = error
  const [verifyState, setVerifyState] = useState(null);
  // For LegiScan-sourced bills (state/national from the new workflow) we have
  // legiscan_bill_id baked into the bill payload, so the official-record
  // section auto-loads on first expand instead of requiring a button click.
  const hasLegiScanId = !!bill.legiscan_bill_id;
  const canVerifyOnLegiScan = (scopeKey === 'state' || scopeKey === 'national') && !hasLegiScanId;

  // Auto-expand when this card is the target of an update highlight or shared focus
  useEffect(() => {
    if (highlight || focused) {
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, focused]);

  // Always register this card's DOM node so the parent can scroll to it (used by
  // ?focus=<bill_key> deep links, ?updates=1 highlights, etc.)
  useEffect(() => {
    if (registerRef && cardRef.current && bill._billKey) {
      registerRef(bill._billKey, cardRef.current);
    }
  }, [bill._billKey, registerRef]);

  // Lazy-load status history the first time the card expands
  useEffect(() => {
    if (!expanded || history !== null || historyLoading) return;
    let cancelled = false;
    setHistoryLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/bills/me/history`, {
          params: { bill_id: bill.bill_id || '', bill_name: bill.name || '', limit: 20 },
        });
        if (!cancelled) setHistory(res.data?.history || []);
      } catch (e) {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Share: copy a deep link with focus=<bill_key>
  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/bills?focus=${encodeURIComponent(bill._billKey || '')}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (err) {
      console.error('Share copy failed', err);
      alert('Could not copy share link.');
    }
  };

  // Trigger LegiScan verification. For LegiScan-sourced bills (which carry
  // legiscan_bill_id), we use the cheap fast-path that skips resolution.
  // For legacy AI-only bills (rare now) we fall back to the slow search path.
  const handleVerifyOnLegiScan = async () => {
    if (verifyState === 'loading' || (typeof verifyState === 'object' && verifyState?.found)) return;
    setVerifyState('loading');
    try {
      const res = await axios.post(`${API_URL}/api/bills/me/verify`, {
        scope: scopeKey,
        bill_id: bill.bill_id || '',
        bill_name: bill.name || '',
        legiscan_bill_id: bill.legiscan_bill_id || undefined,
      });
      setVerifyState(res.data || { found: false });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Verification failed.';
      if (status === 429) {
        setVerifyState(`Quota reached: ${detail}`);
      } else {
        setVerifyState(detail);
      }
    }
  };

  // Auto-load the LegiScan detail the first time a verified bill card expands.
  // We only auto-load when we already have a legiscan_bill_id (so it's a known,
  // exact-match bill and the call is cheap and quota-safe).
  useEffect(() => {
    if (!expanded || !hasLegiScanId || verifyState !== null) return;
    handleVerifyOnLegiScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, hasLegiScanId]);

  const myVote = voteSummary?.my_vote || null;
  const yesCount = voteSummary?.yes || 0;
  const noCount = voteSummary?.no || 0;
  const totalVotes = yesCount + noCount;
  const yesPct = totalVotes > 0 ? Math.round((yesCount / totalVotes) * 100) : 0;
  const noPct = totalVotes > 0 ? 100 - yesPct : 0;

  return (
    <div
      ref={cardRef}
      className={`bg-white border rounded-card shadow-sm hover:shadow-md transition-all ${
        highlight
          ? 'border-amber-300 ring-4 ring-amber-300/60 shadow-lg'
          : focused
          ? 'border-primary/60 ring-2 ring-primary/30 shadow-md'
          : 'border-gray-200'
      }`}
      data-testid="bill-card"
      data-billkey={bill._billKey}
    >
      <div className="flex items-start gap-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left px-5 py-4 flex items-start gap-3"
          data-testid="bill-card-toggle"
        >
          <div className={`mt-0.5 ${scopeColor}`}>
            <MdGavel className="text-2xl" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-rubik font-semibold text-gray-900 text-base leading-snug break-words">
                  {bill.name}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  {bill.bill_id ? (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-mono">
                      {bill.bill_id}
                    </span>
                  ) : null}
                  {bill.status ? (
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      {bill.status}
                    </span>
                  ) : null}
                  {isSubscribed && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                      <MdNotificationsActive className="text-sm" /> Watching
                    </span>
                  )}
                  {highlight && (
                    <span
                      className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1 font-rubik font-semibold"
                      data-testid="bill-update-pill"
                    >
                      <MdNewReleases className="text-sm" /> Just updated
                    </span>
                  )}
                </div>
              </div>
              <div className="text-gray-400 shrink-0">
                {expanded ? <MdExpandLess className="text-2xl" /> : <MdExpandMore className="text-2xl" />}
              </div>
            </div>
            {!expanded && (
              <>
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{bill.summary}</p>
                {totalVotes > 0 && (
                  <div className="mt-2.5 flex items-center gap-2" data-testid="bill-support-mini">
                    <div className="flex-1 h-1.5 rounded-full bg-rose-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${yesPct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 font-rubik tabular-nums shrink-0">
                      {yesPct}% support · {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </button>

        {/* Bell toggle (always visible, top-right) */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSubscribe(bill, !isSubscribed); }}
          disabled={busy}
          className={`mt-3 mr-3 shrink-0 rounded-full p-2 transition-colors ${
            isSubscribed
              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
          } disabled:opacity-50`}
          title={isSubscribed ? 'Stop watching this bill' : 'Notify me when this bill changes status'}
          data-testid="bill-subscribe-toggle"
          aria-pressed={isSubscribed}
        >
          {isSubscribed ? <MdNotificationsActive className="text-xl" /> : <MdNotifications className="text-xl" />}
        </button>
      </div>

      {expanded && (
        <div className="px-5 pb-5 pt-1 space-y-4 border-t border-gray-100" data-testid="bill-card-body">
          {highlight && (
            <div className="rounded-card bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900" data-testid="bill-update-banner">
              <div className="font-rubik font-semibold flex items-center gap-2">
                <MdNewReleases className="text-base" /> Status update
              </div>
              <p className="mt-1 leading-relaxed">
                {highlight.previous_status ? (
                  <>
                    Status changed from <span className="font-semibold">{highlight.previous_status}</span> to{' '}
                    <span className="font-semibold">{highlight.new_status}</span>.
                  </>
                ) : (
                  <>New status: <span className="font-semibold">{highlight.new_status}</span>.</>
                )}
              </p>
            </div>
          )}

          {/* Do you support this? Yes / No vote */}
          <section data-testid="bill-vote-section">
            <div className="flex items-center gap-2 text-gray-700 font-rubik font-semibold text-sm mb-2">
              <MdHowToVote className="text-base" /> Do you support this?
              {totalVotes > 0 && (
                <span className="text-xs font-normal text-gray-400 ml-1">
                  ({totalVotes} {totalVotes === 1 ? 'vote' : 'votes'})
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onVote(bill, myVote === 'yes' ? null : 'yes'); }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-card text-sm font-rubik font-semibold border transition-all ${
                  myVote === 'yes'
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                    : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                }`}
                data-testid="bill-vote-yes"
                aria-pressed={myVote === 'yes'}
              >
                <MdThumbUp className="text-base" />
                Yes
                {yesCount > 0 && <span className="text-xs opacity-80 ml-0.5">· {yesCount}</span>}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onVote(bill, myVote === 'no' ? null : 'no'); }}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-card text-sm font-rubik font-semibold border transition-all ${
                  myVote === 'no'
                    ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                    : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50'
                }`}
                data-testid="bill-vote-no"
                aria-pressed={myVote === 'no'}
              >
                <MdThumbDown className="text-base" />
                No
                {noCount > 0 && <span className="text-xs opacity-80 ml-0.5">· {noCount}</span>}
              </button>
              {myVote && (
                <span className="text-xs text-gray-500 font-rubik italic ml-1">
                  Tap your choice again to clear.
                </span>
              )}
            </div>
            {totalVotes > 0 && (
              <div className="space-y-1" data-testid="bill-support-bar">
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden flex">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${yesPct}%` }}
                    title={`${yesPct}% Yes`}
                  />
                  <div
                    className="h-full bg-rose-500 transition-all"
                    style={{ width: `${noPct}%` }}
                    title={`${noPct}% No`}
                  />
                </div>
                <div className="flex items-center justify-between text-xs font-rubik text-gray-500">
                  <span className="text-emerald-700 font-semibold">{yesPct}% Yes</span>
                  <span className="text-rose-700 font-semibold">{noPct}% No</span>
                </div>
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center gap-2 text-gray-700 font-rubik font-semibold text-sm mb-1">
              <MdInfo className="text-base" /> Summary
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{bill.summary}</p>
          </section>

          <section>
            <div className="flex items-center gap-2 text-emerald-700 font-rubik font-semibold text-sm mb-1">
              <MdThumbUp className="text-base" /> Proponents say
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{bill.proponents}</p>
          </section>

          <section>
            <div className="flex items-center gap-2 text-rose-700 font-rubik font-semibold text-sm mb-1">
              <MdThumbDown className="text-base" /> Opponents say
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{bill.opponents}</p>
          </section>

          {history && history.length > 0 && (
            <section data-testid="bill-history-timeline">
              <div className="flex items-center gap-2 text-gray-700 font-rubik font-semibold text-sm mb-2">
                <MdHistory className="text-base" /> Status history
                <span className="text-xs font-normal text-gray-400">({history.length})</span>
              </div>
              <ol className="relative border-l-2 border-gray-200 ml-2 pl-5 space-y-3">
                {history.map((h) => (
                  <li key={h.notification_id} className="relative">
                    <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-primary ring-4 ring-white" />
                    <div className="text-xs text-gray-400 font-rubik">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </div>
                    <div className="text-sm text-gray-700 font-rubik flex flex-wrap items-center gap-1.5 mt-0.5">
                      {h.previous_status && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                          {h.previous_status}
                        </span>
                      )}
                      <MdArrowForward className="text-gray-400" />
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
                        {h.new_status}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-gray-500 font-rubik font-semibold text-xs uppercase tracking-wide">
                <MdVerified className="text-base text-sky-600" />
                {hasLegiScanId ? 'Official Record (sponsors & full text)' : 'Official Record'}
              </div>
              {canVerifyOnLegiScan && verifyState === null && (
                <button
                  type="button"
                  onClick={handleVerifyOnLegiScan}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-card text-xs font-rubik font-semibold border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
                  data-testid="bill-verify-legiscan-btn"
                >
                  <MdVerified className="text-sm" />
                  Verify with LegiScan
                </button>
              )}
            </div>

            {!hasLegiScanId && !canVerifyOnLegiScan && scopeKey === 'local' && (
              <p className="text-xs text-gray-400 italic">
                Official records are not available for municipal bills via LegiScan.
              </p>
            )}

            {!hasLegiScanId && canVerifyOnLegiScan && verifyState === null && (
              <p className="text-xs text-gray-400 italic">
                Click "Verify with LegiScan" to pull the authoritative state legislative record for this bill.
              </p>
            )}

            {verifyState === 'loading' && (
              <div className="flex items-center gap-2 text-sm text-gray-500 font-rubik" data-testid="bill-verify-loading">
                <MdRefresh className="animate-spin" /> Loading sponsors and official text…
              </div>
            )}

            {typeof verifyState === 'string' && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-card px-3 py-2" data-testid="bill-verify-error">
                {verifyState}
              </div>
            )}

            {verifyState && typeof verifyState === 'object' && verifyState.found === false && (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-card px-3 py-2" data-testid="bill-verify-notfound">
                {verifyState.message || 'No matching bill found in LegiScan for this jurisdiction.'}
                <div className="mt-2 text-[10px] text-gray-400">
                  Data source: <a href={verifyState.attribution?.source_url || 'https://legiscan.com'} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-600">LegiScan</a>
                  {' · '}
                  <a href={verifyState.attribution?.license_url || 'https://creativecommons.org/licenses/by/4.0/'} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-600">CC BY 4.0</a>
                </div>
              </div>
            )}

            {verifyState && typeof verifyState === 'object' && verifyState.found && verifyState.bill && (
              <div className="bg-sky-50/40 border border-sky-200 rounded-card p-4 space-y-3" data-testid="bill-verify-result">
                {/* Mismatch banner only when bill was NOT sourced from LegiScan directly */}
                {!hasLegiScanId && verifyState.title_mismatch && (
                  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-card p-3 flex items-start gap-2" data-testid="bill-verify-mismatch">
                    <MdNewReleases className="text-amber-600 text-lg shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900 font-rubik leading-relaxed">
                      <span className="font-semibold">Heads up:</span> the official record below differs substantially from the AI summary above.
                      The AI may have hallucinated this bill. Please trust the LegiScan record as the authoritative source.
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-rubik font-semibold text-gray-900 break-words">
                      {verifyState.bill.title || 'Untitled bill'}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                      {verifyState.bill.number && (
                        <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-mono">
                          {verifyState.bill.number}
                        </span>
                      )}
                      {verifyState.bill.status && (
                        <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-300 font-semibold">
                          {verifyState.bill.status}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                        <MdVerified className="text-xs" /> Verified
                      </span>
                    </div>
                  </div>
                  {(verifyState.bill.state_link || verifyState.bill.url) && (
                    <a
                      href={verifyState.bill.state_link || verifyState.bill.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-card text-xs font-rubik font-semibold border border-sky-300 bg-white text-sky-700 hover:bg-sky-100"
                      data-testid="bill-verify-statelink"
                    >
                      Read official text <MdOpenInNew className="text-sm" />
                    </a>
                  )}
                </div>

                {verifyState.bill.last_action && (
                  <div className="text-xs text-gray-600 font-rubik">
                    <span className="font-semibold">Last action:</span>{' '}
                    {verifyState.bill.last_action_date ? `${verifyState.bill.last_action_date} — ` : ''}
                    {verifyState.bill.last_action}
                  </div>
                )}

                {verifyState.bill.sponsors && verifyState.bill.sponsors.length > 0 && (
                  <div>
                    <div className="text-xs font-rubik font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Sponsors ({verifyState.bill.sponsor_count || verifyState.bill.sponsors.length})
                    </div>
                    <ul className="space-y-1">
                      {verifyState.bill.sponsors.slice(0, 6).map((s, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-center gap-1.5 font-rubik">
                          <MdPerson className="text-gray-400" />
                          <span className="font-medium">{s.name}</span>
                          {s.party && <span className="text-gray-400">({s.party})</span>}
                          {s.role && <span className="text-gray-500">— {s.role}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-2 border-t border-sky-100 text-[10px] text-gray-500 leading-snug">
                  Legislative data provided by{' '}
                  <a href={verifyState.attribution?.source_url || 'https://legiscan.com'} target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-sky-700">
                    LegiScan
                  </a>{' '}
                  under{' '}
                  <a href={verifyState.attribution?.license_url || 'https://creativecommons.org/licenses/by/4.0/'} target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-sky-700">
                    CC BY 4.0
                  </a>.
                </div>
              </div>
            )}
          </section>

          <section>
            {expanded && (
              <RelatedNews
                endpoint="/api/news/bill"
                params={{
                  bill_id: bill.bill_id || '',
                  bill_name: bill.name || '',
                  state: bill.state || '',
                }}
                limit={4}
                title="Related News"
                testIdPrefix={`bill-news-${bill._billKey || 'unknown'}`}
              />
            )}
          </section>

          {/* Notification controls + Share */}
          <section className="pt-2 border-t border-gray-100" data-testid="bill-notify-controls">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => onToggleSubscribe(bill, !isSubscribed)}
                disabled={busy}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-card text-sm font-rubik border transition-colors disabled:opacity-50 ${
                  isSubscribed
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="bill-subscribe-cta"
              >
                {isSubscribed ? <MdNotificationsActive /> : <MdNotifications />}
                {isSubscribed ? 'Watching status changes' : 'Notify me of status changes'}
              </button>

              {isSubscribed && (
                <button
                  type="button"
                  onClick={() => onToggleEmail(bill, !emailOn)}
                  disabled={busy}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-card text-sm font-rubik border transition-colors disabled:opacity-50 ${
                    emailOn
                      ? 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="bill-email-toggle"
                  aria-pressed={emailOn}
                >
                  {emailOn ? <MdEmail /> : <MdOutlineEmail />}
                  {emailOn ? 'Email alerts on' : 'Also email me'}
                </button>
              )}

              <button
                type="button"
                onClick={handleShare}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-card text-sm font-rubik border transition-colors ${
                  shared
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="bill-share-button"
                title="Copy a link to this bill"
              >
                {shared ? <MdCheck /> : <MdShare />}
                {shared ? 'Link copied!' : 'Share this bill'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              We re-check status daily. In-app notifications fire when a status changes; email is opt-in.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

function ScopeSection({ scope, data, onLoadMore, loadingMore, subscriptionMap, onToggleSubscribe, onToggleEmail, busyKey, highlightMap, registerRef, voteSummaries, onVote, focusedKey, onSearch, searchState }) {
  const Icon = scope.icon;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const canSearch = scope.key === 'state' || scope.key === 'national';
  const searchBusy = searchState?.busy && searchState?.scope === scope.key;
  const searchMessage = searchState && searchState.scope === scope.key ? searchState.message : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    onSearch(scope.key, q);
  };

  if (!data) {
    return (
      <section className="space-y-3" data-testid={`bills-section-${scope.key}`}>
        <div className="flex items-center gap-2">
          <Icon className={`text-2xl ${scope.color}`} />
          <h2 className="font-rubik font-semibold text-gray-900 text-lg">{scope.label}</h2>
        </div>
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-card p-6 text-sm text-gray-500">
          Loading…
        </div>
      </section>
    );
  }

  const bills = data.bills || [];
  const skipped = data.source === 'skipped';
  const errored = data.source === 'error';

  return (
    <section className="space-y-3" data-testid={`bills-section-${scope.key}`}>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className={`text-2xl ${scope.color}`} />
          <h2 className="font-rubik font-semibold text-gray-900 text-lg">{scope.label}</h2>
          <span className="text-xs text-gray-500 ml-1">({bills.length})</span>
        </div>
        <div className="flex items-center gap-3">
          {data.last_updated && !skipped && !errored && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <MdAccessTime />
              <span>{formatRelative(data.last_updated)}</span>
            </div>
          )}
          {canSearch && !skipped && (
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-card text-xs font-rubik font-semibold border transition-colors ${
                searchOpen
                  ? 'bg-gray-100 text-gray-700 border-gray-300'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`bills-search-toggle-${scope.key}`}
              aria-expanded={searchOpen}
            >
              {searchOpen ? <MdClose className="text-sm" /> : <MdSearch className="text-sm" />}
              {searchOpen ? 'Close search' : 'Search bills'}
            </button>
          )}
        </div>
      </div>

      {canSearch && searchOpen && !skipped && (
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 rounded-card p-3 flex items-center gap-2"
          data-testid={`bills-search-form-${scope.key}`}
        >
          <MdSearch className="text-gray-400 text-lg shrink-0" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={`Search ${scope.label.toLowerCase()} by number or keyword (e.g., "HR 1234" or "education funding")`}
            className="flex-1 bg-transparent outline-none text-sm font-rubik text-gray-800 placeholder-gray-400"
            data-testid={`bills-search-input-${scope.key}`}
            maxLength={200}
            disabled={searchBusy}
          />
          <button
            type="submit"
            disabled={searchBusy || !searchInput.trim()}
            className="px-3 py-1.5 rounded-card text-xs font-rubik font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`bills-search-submit-${scope.key}`}
          >
            {searchBusy ? 'Searching…' : 'Search'}
          </button>
        </form>
      )}

      {searchMessage && (
        <div
          className={`text-xs font-rubik rounded-card px-3 py-2 ${
            searchMessage.kind === 'error'
              ? 'bg-rose-50 text-rose-700 border border-rose-200'
              : searchMessage.kind === 'info'
              ? 'bg-gray-50 text-gray-600 border border-gray-200'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`}
          data-testid={`bills-search-message-${scope.key}`}
        >
          {searchMessage.text}
        </div>
      )}

      {skipped && (
        <div className={`${scope.bg} ring-1 ${scope.ring} rounded-card p-5 text-sm text-gray-700`}>
          {data.skipped_reason || 'This scope is unavailable for your profile.'}
        </div>
      )}

      {/* Disclaimer: local bills are AI-only because LegiScan does not cover municipal data */}
      {!skipped && !errored && scope.key === 'local' && bills.length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-card p-3 flex items-start gap-2" data-testid="bills-local-disclaimer">
          <MdInfo className="text-amber-600 text-lg shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 font-rubik leading-relaxed">
            <span className="font-semibold">AI-generated, not independently verified.</span>{' '}
            Local / municipal bills are summarized by AI because no public legislative database (like LegiScan) covers cities and counties. State and national bills below are sourced directly from LegiScan and are authoritative.
          </div>
        </div>
      )}

      {/* Degraded mode: LegiScan was unreachable and we fell back to AI-only for a normally-verified scope */}
      {!skipped && !errored && (scope.key === 'state' || scope.key === 'national') && bills.length > 0 && bills.some((b) => b.source === 'ai-fallback') && (
        <div className="bg-rose-50 border-l-4 border-rose-400 rounded-r-card p-3 flex items-start gap-2" data-testid={`bills-${scope.key}-fallback`}>
          <MdNewReleases className="text-rose-600 text-lg shrink-0 mt-0.5" />
          <div className="text-xs text-rose-900 font-rubik leading-relaxed">
            <span className="font-semibold">Verification temporarily unavailable.</span>{' '}
            LegiScan could not be reached, so these bills are AI-summarized only and have not been verified against the official record. They may contain inaccuracies. Please check back later for the verified version.
          </div>
        </div>
      )}

      {errored && (
        <div className="bg-rose-50 ring-1 ring-rose-200 rounded-card p-5 text-sm text-rose-700">
          {data.error || 'Failed to fetch bills for this scope.'}
        </div>
      )}

      {!skipped && !errored && bills.length === 0 && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-card p-6 text-sm text-gray-500" data-testid={`bills-empty-${scope.key}`}>
          No notable bills returned for this scope. Try "Show more" to ask the AI again.
        </div>
      )}

      <div className="space-y-3">
        {bills.map((b, i) => {
          const key = `${scope.key}|${b._billKey || ''}`;
          const sub = subscriptionMap ? subscriptionMap[key] : null;
          const highlight = highlightMap ? highlightMap[b._billKey] : null;
          const voteSummary = voteSummaries ? voteSummaries[b._billKey] : null;
          const focused = focusedKey && focusedKey === b._billKey;
          return (
            <BillCard
              key={`${scope.key}-${i}-${b.name}`}
              bill={b}
              scopeColor={scope.color}
              scopeKey={scope.key}
              subscription={sub}
              onToggleSubscribe={(bill, want) => onToggleSubscribe(scope.key, bill, want)}
              onToggleEmail={(bill, want) => onToggleEmail(scope.key, bill, want)}
              busy={busyKey === key}
              highlight={highlight}
              registerRef={registerRef}
              voteSummary={voteSummary}
              onVote={(bill, vote) => onVote(scope.key, bill, vote)}
              focused={focused}
            />
          );
        })}
      </div>

      {!skipped && !errored && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => onLoadMore(scope.key)}
            disabled={loadingMore}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-card border border-gray-300 bg-white hover:bg-gray-50 text-sm font-rubik font-medium text-gray-700 disabled:opacity-60"
            data-testid={`load-more-${scope.key}`}
          >
            <MdAdd className="text-base" />
            {loadingMore ? 'Loading more…' : (bills.length === 0 ? 'Try again' : 'Show more bills')}
          </button>
        </div>
      )}
    </section>
  );
}

export default function BillsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [scopes, setScopes] = useState({});
  const [location, setLocation] = useState({});
  const [loadingMoreKey, setLoadingMoreKey] = useState(null);
  // Bill search per-scope: { busy: bool, scope: 'state'|'national'|null, message: { kind, text } | null }
  const [searchState, setSearchState] = useState({ busy: false, scope: null, message: null });
  // Tag-driven discovery: { busy: bool, message: { kind, text, tags? } | null }
  const [discoverState, setDiscoverState] = useState({ busy: false, message: null });
  // subscriptionMap: { `${scope}|${bill_key}` : subscription_record }
  const [subscriptionMap, setSubscriptionMap] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  // highlightMap: { bill_key : { new_status, previous_status, notification_id } }
  const [highlightMap, setHighlightMap] = useState({});
  // voteSummaries: { bill_key : { yes, no, total, my_vote } }
  const [voteSummaries, setVoteSummaries] = useState({});
  const cardRefs = useRef({});
  const didScrollRef = useRef(false);
  const focusedKey = searchParams.get('focus') || null;
  const registerRef = (billKey, el) => {
    if (billKey && el) cardRefs.current[billKey] = el;
  };

  // Attach _billKey to each bill so we can match against subscriptions client-side.
  const augmentBills = async (scopesObj) => {
    const out = {};
    for (const [k, v] of Object.entries(scopesObj || {})) {
      const bills = await Promise.all(
        (v.bills || []).map(async (b) => ({
          ...b,
          _billKey: await computeBillKey(b.bill_id, b.name),
        }))
      );
      out[k] = { ...v, bills };
    }
    return out;
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bills/me/subscriptions`);
      const map = {};
      (res.data?.subscriptions || []).forEach((s) => {
        map[`${s.scope}|${s.bill_key}`] = s;
      });
      setSubscriptionMap(map);
    } catch (e) {
      // Non-fatal — bell will just show "off" everywhere.
      console.warn('Failed to load bill subscriptions', e);
    }
  };

  const fetchVoteSummaries = async (scopesObj) => {
    // Build a flat list of items (one per bill) for the batched summary endpoint.
    const items = [];
    for (const [scopeKey, scopeData] of Object.entries(scopesObj || {})) {
      for (const b of (scopeData?.bills || [])) {
        items.push({
          scope: scopeKey,
          bill_id: b.bill_id || null,
          bill_name: b.name || '',
          bill_key: b._billKey || null,
        });
      }
    }
    if (items.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/bills/votes/summary`, { items });
      setVoteSummaries(res.data?.summaries || {});
    } catch (e) {
      console.warn('Failed to load vote summaries', e);
    }
  };

  const fetchBills = async (refresh = false) => {
    setError(null);
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/bills/me`, {
        params: { scope: 'all', count: 10, refresh },
      });
      const augmented = await augmentBills(res.data.scopes || {});
      setScopes(augmented);
      setLocation(res.data.location || {});
      // Fire-and-forget vote summary fetch
      fetchVoteSummaries(augmented);
    } catch (e) {
      console.error('Failed to fetch bills', e);
      setError(e?.response?.data?.detail || 'Failed to load bills.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBills(false);
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load update highlights when arriving with ?updates=1
  useEffect(() => {
    if (searchParams.get('updates') !== '1') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications`, {
          params: { notification_type: 'bill_status_change', unread_only: true, limit: 50 },
        });
        const notifs = res.data?.notifications || [];
        if (cancelled || notifs.length === 0) return;
        const map = {};
        const ids = [];
        for (const n of notifs) {
          const billId = n.metadata?.bill_id || null;
          const billName = n.metadata?.bill_name || null;
          // Skip if we have neither — can't match
          if (!billId && !billName) continue;
          const key = await computeBillKey(billId, billName);
          map[key] = {
            new_status: n.metadata?.new_status || '',
            previous_status: n.metadata?.previous_status || '',
            notification_id: n.notification_id,
          };
          ids.push(n.notification_id);
        }
        if (!cancelled) setHighlightMap(map);
        // Mark these notifications read after a short delay so the homepage badge clears.
        // The local highlights stay visible until the user navigates away or clears the URL param.
        setTimeout(() => {
          if (ids.length > 0) {
            axios.put(`${API_URL}/api/notifications/mark-read`, { notification_ids: ids }).catch(() => {});
          }
        }, 4000);
      } catch (e) {
        console.warn('Failed to load update highlights', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to first highlighted card once both bills and highlights are loaded.
  useEffect(() => {
    if (didScrollRef.current) return;
    if (Object.keys(highlightMap).length === 0) return;
    if (loading) return;
    // Find first matching bill across scopes
    for (const s of ['local', 'state', 'national']) {
      const bills = scopes[s]?.bills || [];
      for (const b of bills) {
        if (b._billKey && highlightMap[b._billKey]) {
          const el = cardRefs.current[b._billKey];
          if (el) {
            didScrollRef.current = true;
            // Slight delay to let the auto-expand animation settle
            setTimeout(() => {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
            return;
          }
        }
      }
    }
  }, [highlightMap, scopes, loading]);

  // Auto-scroll to ?focus=<bill_key> bill (deep-link from Share)
  useEffect(() => {
    if (didScrollRef.current) return;
    if (!focusedKey) return;
    if (loading) return;
    const el = cardRefs.current[focusedKey];
    if (el) {
      didScrollRef.current = true;
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [focusedKey, scopes, loading]);

  // Clear ?updates=1 from URL after the highlights have rendered, so a refresh
  // doesn't keep auto-scrolling. The visual highlight remains for the session.
  useEffect(() => {
    if (didScrollRef.current && searchParams.get('updates') === '1') {
      const t = setTimeout(() => {
        const sp = new URLSearchParams(searchParams);
        sp.delete('updates');
        setSearchParams(sp, { replace: true });
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [highlightMap, searchParams, setSearchParams]);

  const handleLoadMore = async (scopeKey) => {
    setLoadingMoreKey(scopeKey);
    try {
      const res = await axios.post(`${API_URL}/api/bills/me/load-more`, {
        scope: scopeKey,
        count: 10,
      });
      const added = res.data?.added || [];
      const addedAugmented = await Promise.all(
        added.map(async (b) => ({ ...b, _billKey: await computeBillKey(b.bill_id, b.name) }))
      );
      setScopes((prev) => {
        const cur = prev[scopeKey] || { bills: [] };
        return {
          ...prev,
          [scopeKey]: {
            ...cur,
            bills: [...(cur.bills || []), ...addedAugmented],
            last_updated: res.data.last_updated || cur.last_updated,
          },
        };
      });
    } catch (e) {
      console.error('Load-more failed', e);
      alert(e?.response?.data?.detail || 'Failed to load more bills.');
    } finally {
      setLoadingMoreKey(null);
    }
  };

  // Bill search inside a scope. Prepends matches to the scope's bills list,
  // de-duplicating against what's already there.
  const handleSearch = async (scopeKey, query) => {
    setSearchState({ busy: true, scope: scopeKey, message: null });
    try {
      const res = await axios.post(`${API_URL}/api/bills/me/search`, {
        scope: scopeKey,
        query,
      });
      const added = res.data?.bills || [];
      const addedCount = res.data?.added_count ?? added.length;

      if (!added.length) {
        setSearchState({
          busy: false,
          scope: scopeKey,
          message: {
            kind: 'info',
            text: res.data?.message || 'No matching bills found.',
          },
        });
        return;
      }

      const addedAugmented = await Promise.all(
        added.map(async (b) => ({
          ...b,
          _billKey: await computeBillKey(b.bill_id, b.name),
          _searchHit: true,
        })),
      );

      setScopes((prev) => {
        const cur = prev[scopeKey] || { bills: [] };
        const existingKeys = new Set((cur.bills || []).map((b) => b._billKey));
        const newOnes = addedAugmented.filter((b) => !existingKeys.has(b._billKey));
        return {
          ...prev,
          [scopeKey]: {
            ...cur,
            bills: [...newOnes, ...(cur.bills || [])],
            last_updated: res.data.last_updated || cur.last_updated,
          },
        };
      });

      setSearchState({
        busy: false,
        scope: scopeKey,
        message: {
          kind: 'success',
          text:
            addedCount === 0
              ? res.data?.message || 'Bill already in your list — scroll to find it.'
              : `Added ${addedCount} ${addedCount === 1 ? 'bill' : 'bills'} to your list.`,
        },
      });
    } catch (e) {
      console.error('Search failed', e);
      setSearchState({
        busy: false,
        scope: scopeKey,
        message: {
          kind: 'error',
          text: e?.response?.data?.detail || 'Search failed. Please try again.',
        },
      });
    }
  };

  // Tag-driven bill discovery — pulls in bills matching tags from the user's
  // favorite polls. New bills are auto-prepended to their respective scopes.
  const handleDiscoverFromFavorites = async () => {
    if (discoverState?.busy) return;
    setDiscoverState({ busy: true, message: null });
    try {
      const res = await axios.post(`${API_URL}/api/bills/me/discover-from-favorites`, {});
      const d = res.data || {};
      const totalAdded = d.total_added || 0;
      const addedByScope = d.added_bills_by_scope || {};

      if (d.tags_processed?.length === 0) {
        setDiscoverState({
          busy: false,
          message: {
            kind: 'info',
            text:
              d.message ||
              "You haven't favorited any polls yet. Favorite a few polls so we can learn what topics matter to you.",
          },
        });
        return;
      }

      if (totalAdded === 0) {
        setDiscoverState({
          busy: false,
          message: {
            kind: 'info',
            text:
              d.quota_exhausted
                ? "LegiScan's daily search quota is exhausted — please try again tomorrow."
                : "No new bills matched your interests right now. Your list is already up to date.",
            tags: d.tags_processed || [],
          },
        });
        return;
      }

      // Augment each new bill with a _billKey and prepend to the appropriate scope
      const augmentScope = async (scopeKey) => {
        const arr = addedByScope[scopeKey] || [];
        return Promise.all(
          arr.map(async (b) => ({
            ...b,
            _billKey: await computeBillKey(b.bill_id, b.name),
          })),
        );
      };
      const [stateBills, nationalBills] = await Promise.all([
        augmentScope('state'),
        augmentScope('national'),
      ]);

      setScopes((prev) => {
        const next = { ...prev };
        const mergeInto = (scopeKey, newBills) => {
          if (!newBills.length) return;
          const cur = prev[scopeKey] || { bills: [] };
          const existingKeys = new Set((cur.bills || []).map((b) => b._billKey));
          const additions = newBills.filter((b) => !existingKeys.has(b._billKey));
          next[scopeKey] = {
            ...cur,
            bills: [...additions, ...(cur.bills || [])],
          };
        };
        mergeInto('state', stateBills);
        mergeInto('national', nationalBills);
        return next;
      });

      const billWord = totalAdded === 1 ? 'bill' : 'bills';
      setDiscoverState({
        busy: false,
        message: {
          kind: 'success',
          text: `Added ${totalAdded} new ${billWord} matching your interests${
            d.quota_exhausted
              ? ' (LegiScan quota exhausted before all tags were searched)'
              : ''
          }.`,
          tags: d.tags_processed || [],
        },
      });
    } catch (e) {
      console.error('Discover from favorites failed', e);
      setDiscoverState({
        busy: false,
        message: {
          kind: 'error',
          text: e?.response?.data?.detail || 'Discovery failed. Please try again.',
        },
      });
    }
  };



  const handleToggleSubscribe = async (scopeKey, bill, want) => {
    const key = `${scopeKey}|${bill._billKey}`;
    setBusyKey(key);
    try {
      if (want) {
        const res = await axios.post(`${API_URL}/api/bills/me/subscribe`, {
          scope: scopeKey,
          bill_id: bill.bill_id || null,
          bill_name: bill.name,
          status: bill.status || null,
        });
        setSubscriptionMap((prev) => ({
          ...prev,
          [key]: {
            id: res.data?.id,
            scope: scopeKey,
            bill_key: bill._billKey,
            bill_id: bill.bill_id || null,
            bill_name: bill.name,
            last_known_status: bill.status || null,
            notify_email: false,
          },
        }));
      } else {
        await axios.delete(`${API_URL}/api/bills/me/subscribe`, {
          data: {
            scope: scopeKey,
            bill_id: bill.bill_id || null,
            bill_name: bill.name,
          },
        });
        setSubscriptionMap((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    } catch (e) {
      console.error('Toggle subscribe failed', e);
      alert(e?.response?.data?.detail || 'Failed to update subscription.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleEmail = async (scopeKey, bill, want) => {
    const key = `${scopeKey}|${bill._billKey}`;
    setBusyKey(key);
    try {
      await axios.put(`${API_URL}/api/bills/me/subscribe/email`, {
        scope: scopeKey,
        bill_id: bill.bill_id || null,
        bill_name: bill.name,
        notify_email: want,
      });
      setSubscriptionMap((prev) => {
        const cur = prev[key];
        if (!cur) return prev;
        return { ...prev, [key]: { ...cur, notify_email: want } };
      });
    } catch (e) {
      console.error('Toggle email failed', e);
      alert(e?.response?.data?.detail || 'Failed to update email preference.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleVote = async (scopeKey, bill, vote) => {
    // vote === null → clear; otherwise 'yes' | 'no'
    const billKey = bill._billKey;
    try {
      let res;
      if (vote === null) {
        res = await axios.delete(`${API_URL}/api/bills/me/vote`, {
          data: {
            scope: scopeKey,
            bill_id: bill.bill_id || null,
            bill_name: bill.name,
          },
        });
      } else {
        res = await axios.post(`${API_URL}/api/bills/me/vote`, {
          scope: scopeKey,
          bill_id: bill.bill_id || null,
          bill_name: bill.name,
          vote,
        });
      }
      setVoteSummaries((prev) => ({
        ...prev,
        [billKey]: {
          yes: res.data?.yes ?? 0,
          no: res.data?.no ?? 0,
          total: res.data?.total ?? 0,
          my_vote: res.data?.my_vote ?? null,
          bill_key: billKey,
        },
      }));
    } catch (e) {
      console.error('Vote failed', e);
      alert(e?.response?.data?.detail || 'Failed to record vote.');
    }
  };

  const locationLabel = [location.city, location.state, location.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8" data-testid="bills-page">
        {/* Header */}
        <header className="mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-rubik font-bold text-2xl sm:text-3xl text-gray-900 flex items-center gap-2">
                <MdGavel className="text-primary text-3xl" />
                Bills
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Active legislation that may affect you{locationLabel ? `, based on ${locationLabel}` : ''}.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDiscoverFromFavorites}
                disabled={discoverState?.busy || refreshing || loading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-card border border-primary/30 bg-primary/5 hover:bg-primary/10 text-sm font-rubik text-primary disabled:opacity-60"
                data-testid="bills-discover-button"
                title="Find bills based on the polls you've favorited"
              >
                <MdFavorite className={discoverState?.busy ? 'animate-pulse' : ''} />
                {discoverState?.busy ? 'Discovering…' : 'Discover from favorites'}
              </button>
              <button
                type="button"
                onClick={() => fetchBills(true)}
                disabled={refreshing || loading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-card border border-gray-300 bg-white hover:bg-gray-50 text-sm font-rubik text-gray-700 disabled:opacity-60"
                data-testid="bills-refresh-button"
                title="Re-query the AI for the latest bills"
              >
                <MdRefresh className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </header>

        {discoverState?.message && (
          <div
            className={`mb-4 rounded-card p-4 text-sm font-rubik flex items-start gap-2 ${
              discoverState.message.kind === 'error'
                ? 'bg-rose-50 ring-1 ring-rose-200 text-rose-700'
                : discoverState.message.kind === 'info'
                ? 'bg-gray-50 ring-1 ring-gray-200 text-gray-700'
                : 'bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800'
            }`}
            data-testid="bills-discover-result"
          >
            <MdFavorite className="text-lg shrink-0 mt-0.5" />
            <div>
              {discoverState.message.text}
              {discoverState.message.tags?.length > 0 && (
                <div className="mt-1 text-xs opacity-80">
                  Based on tags: {discoverState.message.tags.map((t) => `'${t}'`).join(', ')}
                </div>
              )}
            </div>
          </div>
        )}

        <TrendingTicker kind="bills" hours={24} limit={2} />

        {error && (
          <div className="mb-4 bg-rose-50 ring-1 ring-rose-200 rounded-card p-4 text-sm text-rose-700" data-testid="bills-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-6">
            {SCOPES.map((s) => (
              <ScopeSection
                key={s.key}
                scope={s}
                data={null}
                onLoadMore={() => {}}
                loadingMore={false}
                subscriptionMap={subscriptionMap}
                onToggleSubscribe={handleToggleSubscribe}
                onToggleEmail={handleToggleEmail}
                busyKey={busyKey}
                highlightMap={highlightMap}
                registerRef={registerRef}
                voteSummaries={voteSummaries}
                onVote={handleVote}
                focusedKey={focusedKey}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {SCOPES.map((s) => (
              <ScopeSection
                key={s.key}
                scope={s}
                data={scopes[s.key]}
                onLoadMore={handleLoadMore}
                loadingMore={loadingMoreKey === s.key}
                subscriptionMap={subscriptionMap}
                onToggleSubscribe={handleToggleSubscribe}
                onToggleEmail={handleToggleEmail}
                busyKey={busyKey}
                highlightMap={highlightMap}
                registerRef={registerRef}
                voteSummaries={voteSummaries}
                onVote={handleVote}
                focusedKey={focusedKey}
                onSearch={handleSearch}
                searchState={searchState}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
